// 자유 스탯 배분 저장(dungeonhero.stats.v1, 레인 C — docs/PC-WINDOWS-PRD.md 3.3). 기존 저장 키는 바꾸지 않는다(새 키 하나).
// 형식: { version: 1, rev, by, slots: { "<영웅>:<환생 회차>": { str, dex, int, vit } } }. 배분은 "이 값으로 맞춘다"라 같은 명령을 두 번 적용해도 같다(멱등).
// 다른 탭과는 rev가 큰 쪽이 이긴다(rebirth.v1·growth.v2 랭크와 같은 규칙). 손상은 .bak/.latest에 남기고 .good으로 잇고, 미래 버전은 읽기만 한다.
// 포인트(레벨)는 성장 장부가 정본이라 저장하지 않는다: 저장된 배분이 지금 포인트보다 많으면 효과만 비례로 줄인다(shared/stats fitStats).
// 캐릭터별 키(#214)는 CharacterStorage가 SYNC_KEYS 등록만으로 바꿔 끼운다(account/keys.ts).
import {
  EMPTY_STATS, STAT_TEXT, applyStats, fitStats, statAllocValid, statPointsAt, statSum,
  type StatAlloc, type StatCode, type StatLastView, type StatSaveMode, type StatView,
} from '../shared/stats';
import { Scene, type GameCommand } from '../shared/types';
import type { GrowthMods } from '../shared/growth';
import { rewardId, type SaveStorage } from './save';
import type { GameSim } from './game';

export const STATS_KEY = 'dungeonhero.stats.v1';
export const STATS_VERSION = 1;
export const STATS_BACKUP_KEY = STATS_KEY + '.bak';
export const STATS_LATEST_BACKUP_KEY = STATS_BACKUP_KEY + '.latest';
export const STATS_GOOD_KEY = STATS_KEY + '.good';
const WRITE_ATTEMPTS = 5;
const SEEN_LIMIT = 64;
/** 저장에 둘 수 있는 칸 수 상한(영웅 5 × 회차 3 여유) */
const SLOT_CAP = 32;
const SLOT_RE = /^(kael|lyra|seren|brann|lumen):[0-9]{1,2}$/;

export interface StatsSave { version: number; rev: number; by: string; slots: Record<string, StatAlloc>; /** 이 빌드가 모르는 칸(이후 빌드의 영웅 등) — 원문 그대로 보존해 다시 쓴다 */ other?: Record<string, unknown> }
export function freshStatsSave(): StatsSave { return { version: STATS_VERSION, rev: 0, by: '', slots: {} }; }
export function slotKey(hero: string, cycle: number): string { return hero + ':' + Math.max(0, Math.floor(cycle)); }

const record = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const nonNeg = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;

type Parsed = { ok: true; save: StatsSave } | { ok: false; reason: 'corrupt' | 'future' };
/** 원문 → 저장. JSON·타입·버전이 깨진 것만 손상. 모르는 칸 이름은 버린다(다른 빌드) */
export function parseStats(raw: string): Parsed {
  let p: unknown;
  try { p = JSON.parse(raw); } catch { return { ok: false, reason: 'corrupt' }; }
  if (!record(p)) return { ok: false, reason: 'corrupt' };
  if (typeof p.version === 'number' && p.version > STATS_VERSION) return { ok: false, reason: 'future' };
  if (p.version !== STATS_VERSION || !nonNeg(p.rev) || typeof p.by !== 'string' || !record(p.slots)) return { ok: false, reason: 'corrupt' };
  const slots: Record<string, StatAlloc> = {};
  const other: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p.slots)) {
    if (!SLOT_RE.test(k)) { other[k] = v; continue; }
    if (!record(v) || !nonNeg(v.str) || !nonNeg(v.dex) || !nonNeg(v.int) || !nonNeg(v.vit)) return { ok: false, reason: 'corrupt' };
    slots[k] = { str: v.str, dex: v.dex, int: v.int, vit: v.vit };
  }
  return { ok: true, save: { version: STATS_VERSION, rev: p.rev, by: p.by, slots, other } };
}
export function serializeStats(s: StatsSave): string {
  return JSON.stringify({ version: STATS_VERSION, rev: s.rev, by: s.by, slots: { ...s.other, ...s.slots } });
}

type ReadResult = { kind: 'none' } | { kind: 'ok'; save: StatsSave } | { kind: 'corrupt'; raw: string } | { kind: 'future' } | { kind: 'unavailable' };

/** 저장 모듈. 마을 명령 단위로만 쓴다(프레임 루프 아님) */
export class StatsStore {
  mode: StatSaveMode = 'ok';
  revision = 0;
  private confirmed: StatsSave = freshStatsSave();
  private readonly nonce: string;
  private readonly seenReq = new Set<string>();

  constructor(private readonly storage: SaveStorage, options: { nonce?: string } = {}) {
    this.nonce = options.nonce ?? rewardId();
    this.refresh();
  }
  get state(): StatsSave { return this.confirmed; }
  alloc(hero: string, cycle: number): StatAlloc { return this.confirmed.slots[slotKey(hero, cycle)] ?? EMPTY_STATS; }

  private read(): ReadResult {
    let raw: string | null;
    try { raw = this.storage.getItem(STATS_KEY); } catch { return { kind: 'unavailable' }; }
    if (raw === null) return { kind: 'none' };
    const p = parseStats(raw);
    if (p.ok) return { kind: 'ok', save: p.save };
    return p.reason === 'future' ? { kind: 'future' } : { kind: 'corrupt', raw };
  }
  private readGood(): StatsSave | null {
    try {
      const raw = this.storage.getItem(STATS_GOOD_KEY);
      if (raw === null) return null;
      const p = parseStats(raw);
      return p.ok ? p.save : null;
    } catch { return null; }
  }
  private backupCorrupt(raw: string): boolean {
    try {
      const first = this.storage.getItem(STATS_BACKUP_KEY);
      if (first === null) this.storage.setItem(STATS_BACKUP_KEY, raw);
      else if (first !== raw && this.storage.getItem(STATS_LATEST_BACKUP_KEY) !== raw) this.storage.setItem(STATS_LATEST_BACKUP_KEY, raw);
      return this.storage.getItem(STATS_BACKUP_KEY) === raw || this.storage.getItem(STATS_LATEST_BACKUP_KEY) === raw;
    } catch { return false; }
  }
  private setMode(m: StatSaveMode): void { if (this.mode !== m) { this.mode = m; this.revision++; } }

  /** 저장소 최신으로 맞춘다. rev가 큰 쪽(같으면 저장소). 손상이면 백업 뒤 정상 사본으로 잇는다. 바뀌었으면 true */
  refresh(): boolean {
    const before = this.confirmed;
    const r = this.read();
    switch (r.kind) {
      case 'unavailable': this.setMode('unavailable'); return false;
      case 'future': this.setMode('future'); return false;
      case 'none': this.setMode('ok'); break;
      case 'ok': if (r.save.rev >= this.confirmed.rev) this.confirmed = r.save; this.setMode('ok'); break;
      case 'corrupt': {
        if (!this.backupCorrupt(r.raw)) { this.setMode('unavailable'); return false; }
        const good = this.readGood();
        if (!good) { this.setMode('corrupt'); return false; }
        if (good.rev >= this.confirmed.rev) this.confirmed = good;
        this.setMode('ok');
        try { this.storage.setItem(STATS_KEY, serializeStats(this.confirmed)); } catch { /* 다음 쓰기에서 */ }
        break;
      }
    }
    if (this.confirmed !== before) { this.revision++; return true; }
    return false;
  }

  markReq(requestId: string): boolean {
    if (this.seenReq.has(requestId)) return false;
    this.seenReq.add(requestId);
    if (this.seenReq.size > SEEN_LIMIT) { const o = this.seenReq.values().next(); if (!o.done) this.seenReq.delete(o.value); }
    return true;
  }

  /** 한 칸을 alloc으로 맞춘다(포인트 검사는 호출자). 저장소 최신 위에 쓰고 되읽어 확인한다 */
  set(requestId: string, slot: string, alloc: StatAlloc): StatCode {
    if (!this.markReq(requestId)) return 'duplicate';
    this.refresh();
    if (this.mode !== 'ok') return 'readonly';
    for (let attempt = 0; attempt < WRITE_ATTEMPTS; attempt++) {
      const slots = { ...this.confirmed.slots };
      if (statSum(alloc) === 0) delete slots[slot]; else slots[slot] = { str: alloc.str, dex: alloc.dex, int: alloc.int, vit: alloc.vit };
      if (Object.keys(slots).length + Object.keys(this.confirmed.other ?? {}).length > SLOT_CAP) return 'bad_alloc';
      const next: StatsSave = { version: STATS_VERSION, rev: this.confirmed.rev + 1, by: this.nonce + ':' + requestId, slots, other: this.confirmed.other };
      const json = serializeStats(next);
      try { this.storage.setItem(STATS_KEY, json); } catch { return 'save_failed'; }
      try { this.storage.setItem(STATS_GOOD_KEY, json); } catch { /* 사본 실패는 주 저장 성공을 바꾸지 않는다 */ }
      const back = this.read();
      if (back.kind === 'ok' && back.save.by === next.by) { this.confirmed = back.save; this.revision++; return 'ok'; }
      if (back.kind === 'ok') { this.confirmed = back.save; continue; } // 다른 탭이 사이에 덮어씀: 그 위에 다시
      return 'save_failed';
    }
    return 'save_failed';
  }
}

type StatCmd = Extract<GameCommand, { type: 'statAlloc' }>;
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** GameSim 연결: 뷰(GameView.stats)·명령·전투 보정 */
export class StatController {
  readonly store: StatsStore;
  readonly last: Mutable<StatLastView> = { seq: 0, code: 'ok', requestId: '', message: '' };
  readonly view: StatView;
  /** 효과에 쓰는 배분(재사용 객체) */
  private readonly eff = { str: 0, dex: 0, int: 0, vit: 0 };

  constructor(private readonly g: GameSim, storage: SaveStorage, options: { nonce?: string } = {}) {
    this.store = new StatsStore(storage, options);
    const self = this;
    this.view = {
      get mode() { return self.store.mode; },
      get revision() { return self.store.revision + self.last.seq * 1000 + self.points * 7; },
      get points() { return self.points; },
      get alloc() { return self.effective(); },
      get free() { return Math.max(0, self.points - statSum(self.effective())); },
      last: this.last,
    };
  }

  get points(): number { return statPointsAt(this.g.growth.level); }
  private slot(): string { return slotKey(this.g.heroId, this.g.rebirth.cur.cycle); }
  /** 지금 영웅·회차의 효과 배분(포인트 안으로) */
  effective(): StatAlloc { return fitStats(this.eff, this.store.alloc(this.g.heroId, this.g.rebirth.cur.cycle), this.points); }

  /** growthMods 직후에 부른다(syncGrowth·레벨업) */
  applyTo(out: GrowthMods): void { applyStats(out, this.effective()); }

  /** 다른 탭이 바꾼 배분을 읽어 들인다(마을 틱에서 드물게). 바뀌었으면 true */
  refresh(): boolean { return this.store.refresh(); }

  /** 명령: 배분을 그 값으로 맞춘다. 마을에서, 일시정지가 아닐 때만 */
  command(cmd: StatCmd): void {
    const f = this.g.flow;
    let code: StatCode;
    const a = cmd.alloc;
    if (f.paused) code = this.store.markReq(cmd.requestId) ? 'paused' : 'duplicate';
    else if (f.scene !== Scene.Village) code = this.store.markReq(cmd.requestId) ? 'no_menu' : 'duplicate';
    else if (!a || !statAllocValid(a, this.points)) code = this.store.markReq(cmd.requestId) ? 'bad_alloc' : 'duplicate';
    else code = this.store.set(cmd.requestId, this.slot(), a);
    if (code === 'readonly' && this.store.mode === 'corrupt') code = 'corrupt';
    if (code === 'duplicate') return;
    const l = this.last;
    l.seq++; l.code = code; l.requestId = cmd.requestId; l.message = STAT_TEXT[code];
    if (code === 'ok') this.g.syncGrowth();
  }
}
